import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTicket, markTicketScanned } from '../services/api';
import './TicketView.css';

function TicketView() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTicket();
  }, [ticketId]);

  useEffect(() => {
    const markScanned = async () => {
      try {
        await markTicketScanned(ticketId);
      } catch (error) {
        // Non-blocking: ticket can still be viewed even if scan mark fails
        console.error('Failed to mark ticket as scanned:', error);
      }
    };

    if (ticketId) {
      markScanned();
    }
  }, [ticketId]);

  const loadTicket = async () => {
    try {
      const response = await getTicket(ticketId);
      setTicket(response.data);
    } catch (error) {
      toast.error('Failed to load ticket');
      console.error(error);
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintTicket = () => {
    const printWindow = window.open('', '', 'width=400,height=600');
    const ticketElement = document.getElementById('printable-ticket');
    const ticketHTML = ticketElement.innerHTML;
    
    const thermalHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Ticket</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Courier New', monospace;
            width: 80mm;
            padding: 0;
            background: white;
          }
          
          .thermal-ticket {
            width: 80mm;
            padding: 10mm;
            text-align: center;
            font-size: 12pt;
            line-height: 1.4;
          }
          
          .ticket-header {
            margin-bottom: 8mm;
            border-bottom: 2px solid #000;
            padding-bottom: 5mm;
          }
          
          .ticket-header h2 {
            font-size: 16pt;
            font-weight: bold;
            margin-bottom: 3mm;
          }
          
          .ticket-number {
            font-size: 28pt;
            font-weight: bold;
            letter-spacing: 2px;
          }
          
          .ticket-body {
            margin-top: 8mm;
          }
          
          .ticket-info {
            text-align: left;
            margin-bottom: 8mm;
            font-size: 11pt;
          }
          
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3mm;
            padding: 2mm 0;
            border-bottom: 1px dotted #ccc;
          }
          
          .info-label {
            font-weight: bold;
            width: 40%;
          }
          
          .info-value {
            width: 60%;
            text-align: right;
            word-wrap: break-word;
          }
          
          .queue-num {
            font-size: 14pt;
            font-weight: bold;
          }
          
          .qr-code {
            margin: 8mm 0;
            text-align: center;
          }
          
          .qr-code img {
            width: 50mm;
            height: 50mm;
          }
          
          .ticket-instructions {
            margin-top: 8mm;
            padding-top: 5mm;
            border-top: 2px solid #000;
            font-size: 10pt;
            text-align: center;
            line-height: 1.6;
          }
          
          .ticket-instructions p {
            margin: 2mm 0;
          }
          
          @media print {
            body {
              margin: 0;
              padding: 0;
              width: 80mm;
            }
            .thermal-ticket {
              margin: 0;
              padding: 10mm;
              width: 80mm;
            }
          }
        </style>
      </head>
      <body>
        <div class="thermal-ticket">
          <div class="ticket-header">
            <h2>Navbat</h2>
            <div class="ticket-number">${ticket.ticket_number}</div>
          </div>
          
          <div class="ticket-body">
            <div class="ticket-info">
              <div class="info-row">
                <span class="info-label">Fakultet:</span>
                <span class="info-value">${ticket.faculty_name}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Bo'lim:</span>
                <span class="info-value">${ticket.section_name}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Xizmat:</span>
                <span class="info-value">${ticket.service_name}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Navbat:</span>
                <span class="info-value queue-num">${ticket.queue_number}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Vaqt:</span>
                <span class="info-value">${new Date(ticket.created_at).toLocaleString()}</span>
              </div>
              ${ticket.student_name ? `
              <div class="info-row">
                <span class="info-label">Ism:</span>
                <span class="info-value">${ticket.student_name}</span>
              </div>
              ` : ''}
            </div>
            
            <div class="ticket-instructions">
              <p>Iltimos, navbatingizni kuting.</p>
              <p>Ekrandan yangilanishni ko'rishingiz mumkin.</p>
            </div>
          </div>
        </div>
        
        <script>
          window.onload = function() {
            // Auto-print with thermal printer settings
            setTimeout(function() {
              window.print();
              // Close after printing
              setTimeout(function() {
                window.close();
              }, 500);
            }, 100);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(thermalHTML);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="ticket-view">
        <div className="loading">
          <p>Navbat yuklanmoqda...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-view">
        <div className="error">
          <p>Navbat topilmadi</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-view">
      <div className="ticket-container">
        <div className="ticket-display" id="printable-ticket">
          <div className="ticket-header">
            <h2>Navbat</h2>
            <div className="ticket-number">{ticket.ticket_number}</div>
          </div>
          
          <div className="ticket-body">
            <div className="ticket-info">
              <div className="info-row">
                <span className="info-label">Fakultet:</span>
                <span className="info-value">{ticket.faculty_name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Bo'lim:</span>
                <span className="info-value">{ticket.section_name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Xizmat:</span>
                <span className="info-value">{ticket.service_name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Navbat:</span>
                <span className="info-value queue-num">{ticket.queue_number}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Vaqt:</span>
                <span className="info-value">
                  {new Date(ticket.created_at).toLocaleString()}
                </span>
              </div>
              {ticket.student_name && (
                <div className="info-row">
                  <span className="info-label">Ism:</span>
                  <span className="info-value">{ticket.student_name}</span>
                </div>
              )}
            </div>
            
            <div className="ticket-instructions">
              <p>Iltimos, navbatingizni kuting.</p>
              <p>Ekrandan yangilanishni ko'rishingiz mumkin.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketView;
